import { BarCustomLayerProps, ResponsiveBar } from "@nivo/bar";
import { useWindowSize } from "hooks/useWindowSize";
import ToolTip from "components/design/toolTip";
import {
  BAR_GAP,
  BAR_RADIUS,
  DOT_SIZE,
  EXPENSE_BAR_COLOR,
  EXPENSE_BAR_CROSS_LINE_COLOR,
  EXPENSE_BAR_CROSS_LINE_OPACITY,
  LINE_SPACING,
  PROFIT_LOSS_LINE_COLOR,
  REVENUE_BAR_COLOR,
  REVENUE_BAR_CROSS_LINE_COLOR,
  REVENUE_BAR_CROSS_LINE_OPACITY,
} from "constants/booksHomePage";
import { colors } from "constants/colors";
import { MM_SPACE_YYYY } from "constants/date";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import {
  ForecastSubtitle,
  TrendSubtitle,
} from "pages/Books/FinancialsSummary/FinancialSummary";
import { memo, ReactNode, useMemo } from "react";
import { Metrics, RevenueMetrics } from "store/apis/metrics";
import { formatAmount } from "utils/foramtAmount";
import {
  BarChartConfig,
  BarForecastMeta,
  ChartItemInfo,
  ForecastLegend,
  ForecastMetaByDate,
  GenericBarChartData,
  Legend,
  LegendDot,
  buildForecastMetaByDate,
} from "../ChartComponents";
import { HideableAmount } from "../HideBalancesAndAmounts";
import { CASH_FLOW_CONFIG } from "./cashBalanceChartConstants";

type AmountClickHandler = (type: "revenue" | "expense", date: string) => void;

const ClickableAmount = ({
  onClick,
  children,
}: {
  onClick?: () => void;
  children: ReactNode;
}) => {
  if (!onClick) {
    return <HideableAmount>{children}</HideableAmount>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="all:unset t-cursor-pointer t-text-white !t-underline t-decoration-neutral-40 hover:t-decoration-white t-transition"
    >
      <HideableAmount>{children}</HideableAmount>
    </button>
  );
};

export const InfoToolTip = ({
  data,
  config,
  children,
  onAmountClick,
  forecastMeta,
}: {
  data: GenericBarChartData;
  config: BarChartConfig;
  children: ReactNode;
  onAmountClick?: AmountClickHandler;
  forecastMeta?: BarForecastMeta;
}) => {
  const isNegative = (data.lineValue ?? 0) < 0;
  const lineLabel =
    isNegative && config.labels.lineNegative
      ? config.labels.lineNegative
      : config.labels.line;

  const isForecast = Boolean(forecastMeta?.isForecast);
  const hasOverlapActual = Boolean(forecastMeta?.hasOverlapActual);
  const bar1Label = isForecast
    ? `${config.labels.bar1} (Forecast)`
    : config.labels.bar1;
  const bar2Label = isForecast
    ? `${config.labels.bar2} (Forecast)`
    : config.labels.bar2;
  const clickableOnAmountClick = isForecast ? undefined : onAmountClick;

  return (
    <ToolTip
      propClass="!t-bg-neutral-100"
      text={
        <div className="t-text-start t-flex t-flex-col t-gap-2 t-text-body-sm">
          <div className="t-text-white">
            {dayjs(data.date).format(MM_SPACE_YYYY)}
          </div>
          <ChartItemInfo
            dot={<LegendDot color={config.colors.bar1} />}
            lable={bar1Label}
            value={
              <ClickableAmount
                onClick={
                  clickableOnAmountClick
                    ? () => clickableOnAmountClick("revenue", data.date)
                    : undefined
                }
              >
                {formatAmount(Number(data.bar1 || 0))}
              </ClickableAmount>
            }
          />
          {hasOverlapActual && forecastMeta?.actualBar1 !== undefined && (
            <ChartItemInfo
              dot={<LegendDot color={config.colors.bar1} />}
              lable={`${config.labels.bar1} (Actual)`}
              value={
                <HideableAmount>
                  {formatAmount(Number(forecastMeta.actualBar1 || 0))}
                </HideableAmount>
              }
            />
          )}
          {config.labels.bar2 && (
            <ChartItemInfo
              dot={<LegendDot color={config.colors.bar2} />}
              lable={bar2Label}
              value={
                <ClickableAmount
                  onClick={
                    clickableOnAmountClick
                      ? () => clickableOnAmountClick("expense", data.date)
                      : undefined
                  }
                >
                  {formatAmount(Number(data.bar2 || 0))}
                </ClickableAmount>
              }
            />
          )}
          {hasOverlapActual &&
            config.labels.bar2 &&
            forecastMeta?.actualBar2 !== undefined && (
              <ChartItemInfo
                dot={<LegendDot color={config.colors.bar2} />}
                lable={`${config.labels.bar2} (Actual)`}
                value={
                  <HideableAmount>
                    {formatAmount(Number(forecastMeta.actualBar2 || 0))}
                  </HideableAmount>
                }
              />
            )}
          {data.lineValue !== undefined && (
            <ChartItemInfo
              dot={<LegendDot color={config.colors.line} />}
              lable={isForecast ? `${lineLabel} (Forecast)` : lineLabel}
              value={
                <HideableAmount>
                  {formatAmount(Number(data.lineValue || 0))}
                </HideableAmount>
              }
            />
          )}
          {hasOverlapActual && forecastMeta?.actualLineValue !== undefined && (
            <ChartItemInfo
              dot={<LegendDot color={config.colors.line} />}
              lable={`${lineLabel} (Actual)`}
              value={
                <HideableAmount>
                  {formatAmount(Number(forecastMeta.actualLineValue || 0))}
                </HideableAmount>
              }
            />
          )}
        </div>
      }
    >
      {children}
    </ToolTip>
  );
};

const BarLayer = memo(
  ({
    props,
    config,
    onAmountClick,
    forecastMetaByDate,
  }: {
    props: BarCustomLayerProps<GenericBarChartData>;
    config: BarChartConfig;
    onAmountClick?: AmountClickHandler;
    forecastMetaByDate?: ForecastMetaByDate;
  }) => {
    const bar1Bars = props.bars.filter((bar) => bar.data.id === "bar1");
    const bar2Bars = props.bars.filter((bar) => bar.data.id === "bar2");

    const barwidth = ({
      numberofColumns,
      width,
    }: {
      numberofColumns: number;
      width: number;
    }) => {
      switch (numberofColumns) {
        case 3:
          return width * 0.5;
        case 6:
          return width * 0.6;
        default:
          return width;
      }
    };

    const xPoint = ({
      numberofColumns,
      x,
      width,
    }: {
      numberofColumns: number;
      x: number;
      width: number;
    }) => {
      switch (numberofColumns) {
        case 3:
          return x + width * 0.25;
        case 6:
          return x + width * 0.2;
        default:
          return x;
      }
    };

    const barsByIndex = new Map<number, (typeof bar1Bars)[0]>();
    [...bar1Bars, ...bar2Bars].forEach((bar) => {
      if (!barsByIndex.has(bar.data.index)) {
        barsByIndex.set(bar.data.index, bar);
      }
    });

    const orderedBars = [...barsByIndex.values()].sort(
      (a, b) => a.data.index - b.data.index
    );
    const firstForecastIndex = orderedBars.findIndex(
      (bar) => forecastMetaByDate?.[bar.data.data.date]?.isForecast
    );
    const firstForecastBar =
      firstForecastIndex >= 0 ? orderedBars[firstForecastIndex] : undefined;
    const lastActualBar =
      firstForecastIndex > 0 ? orderedBars[firstForecastIndex - 1] : undefined;
    const forecastBoundaryX = firstForecastBar
      ? lastActualBar
        ? (lastActualBar.x + lastActualBar.width + firstForecastBar.x) / 2
        : firstForecastBar.x
      : null;
    const hasLineValueSeries = orderedBars.some(
      (bar) => bar.data.data.lineValue !== undefined
    );

    return (
      <>
        {/* Forecast period band */}
        {forecastBoundaryX !== null && (
          <>
            <ToolTip propClass="!t-bg-neutral-100" text="Forecasted period">
              <rect
                x={forecastBoundaryX}
                y={0}
                width={Math.max(0, props.innerWidth - forecastBoundaryX)}
                height={props.innerHeight}
                fill={colors.green[10]}
              />
            </ToolTip>
            <line
              x1={forecastBoundaryX}
              x2={forecastBoundaryX}
              y1={0}
              y2={props.innerHeight}
              stroke={colors.neutral[40]}
              strokeWidth={1}
              strokeDasharray="3 3"
            />
          </>
        )}
        {/* Bars */}
        {[...bar1Bars, ...bar2Bars].map(
          ({ x, y, width, height, color, key, label, data }) => {
            if (!width || !height) return null;

            const isBar1 = data.id === "bar1";
            const numberofColumns = isBar1 ? bar1Bars.length : bar2Bars.length;
            const forecastMeta = forecastMetaByDate?.[data.data.date];
            const isForecast = Boolean(forecastMeta?.isForecast);
            const hasOverlapActual = Boolean(forecastMeta?.hasOverlapActual);

            const responsiveWidth = barwidth({ numberofColumns, width });
            const responsiveX = xPoint({ numberofColumns, x, width });
            const finalHeight = Math.max(0, Math.abs(height) - BAR_GAP);

            if (finalHeight <= 0 || !responsiveWidth) return null;
            const crossLineColor = isBar1
              ? config.colors.bar1CrossLine
              : config.colors.bar2CrossLine;
            const crossLineOpacity = isBar1
              ? config.colors.bar1CrossLineOpacity ??
                REVENUE_BAR_CROSS_LINE_OPACITY
              : config.colors.bar2CrossLineOpacity ??
                EXPENSE_BAR_CROSS_LINE_OPACITY;

            const maxLines = 50;
            const lines = Array.from({ length: maxLines }, (_, index) => index);

            const actualValue = isBar1
              ? forecastMeta?.actualBar1
              : forecastMeta?.actualBar2;
            const overlapActualHeight =
              hasOverlapActual && actualValue !== undefined
                ? Math.max(
                    0,
                    Math.abs(props.yScale(0) - props.yScale(actualValue)) -
                      BAR_GAP
                  )
                : 0;
            const overlapActualWidth = responsiveWidth * 0.4;

            return (
              <motion.g
                key={key}
                initial={{ x: responsiveX, y: y }}
                animate={{ x: responsiveX, y: y }}
                aria-label={label}
              >
                <InfoToolTip
                  data={data.data}
                  config={config}
                  onAmountClick={onAmountClick}
                  forecastMeta={forecastMeta}
                  key={key}
                >
                  <motion.rect
                    initial={{ height: "100%" }}
                    animate={{ height: finalHeight, width: responsiveWidth }}
                    fill={color}
                    fillOpacity={isForecast ? 0.5 : 1}
                    strokeWidth={isForecast ? 1.25 : 0}
                    stroke={color}
                    strokeDasharray={isForecast ? "3 2" : undefined}
                    focusable="false"
                    rx={props.borderRadius}
                    ry={props.borderRadius}
                  />
                </InfoToolTip>
                {hasOverlapActual && overlapActualHeight > 0 && (
                  <rect
                    x={responsiveWidth - overlapActualWidth}
                    y={finalHeight - overlapActualHeight}
                    width={overlapActualWidth}
                    height={overlapActualHeight}
                    fill={color}
                    rx={props.borderRadius}
                    ry={props.borderRadius}
                  />
                )}
                <svg
                  x={0}
                  y={0}
                  width={responsiveWidth}
                  height={finalHeight}
                  overflow="hidden"
                >
                  {lines.map((_, index) => (
                    <motion.line
                      key={`line-${index}`}
                      initial={{ x1: 0, x2: 0, y1: 0, y2: 0 }}
                      animate={{
                        x1: 0,
                        x2: responsiveWidth * 1.5,
                        y1: -0.76,
                        y2: -0.76,
                      }}
                      transition={{ delay: 0.1 }}
                      transform={`matrix(0.73343 -0.679765 0.73343 0.679765 -1.45459 ${
                        index * LINE_SPACING + 1.095
                      })`}
                      stroke={crossLineColor}
                      strokeOpacity={crossLineOpacity}
                      strokeWidth={1.52}
                    />
                  ))}
                </svg>
              </motion.g>
            );
          }
        )}

        {/* Dots and connecting dotted lines based on cash_balance (lineValue) */}
        {bar1Bars.map(({ x, width, key, data }, index) => {
          const cashBalance = data.data.lineValue;

          if (cashBalance === undefined) return null;

          const forecastMeta = forecastMetaByDate?.[data.data.date];
          const isForecast = Boolean(forecastMeta?.isForecast);
          const hasOverlapActual = Boolean(forecastMeta?.hasOverlapActual);
          const actualLineValue = forecastMeta?.actualLineValue;

          const cx = x + width / 2;
          const cy = props.yScale(cashBalance);

          const nextBar = bar1Bars[index + 1];
          const nextCashBalance = nextBar?.data.data.lineValue;
          const hasNextPoint = nextBar && nextCashBalance !== undefined;
          const nextIsForecast = Boolean(
            nextBar && forecastMetaByDate?.[nextBar.data.data.date]?.isForecast
          );

          const nextCx = hasNextPoint ? nextBar.x + nextBar.width / 2 : 0;
          const nextCy = hasNextPoint ? props.yScale(nextCashBalance) : 0;

          return (
            <motion.g key={`dot-${key}`}>
              {hasNextPoint && (
                <motion.line
                  strokeDasharray={isForecast || nextIsForecast ? "5 4" : "4 4"}
                  x1={cx}
                  y1={cy}
                  x2={nextCx}
                  y2={nextCy}
                  stroke={config.colors.line}
                  strokeWidth={2}
                  strokeOpacity={isForecast || nextIsForecast ? 0.7 : 1}
                />
              )}
              <InfoToolTip
                data={data.data}
                config={config}
                onAmountClick={onAmountClick}
                forecastMeta={forecastMeta}
              >
                <circle
                  cx={cx}
                  cy={cy}
                  r={DOT_SIZE / 2}
                  fill={config.colors.line}
                  fillOpacity={isForecast ? 0.7 : 1}
                  stroke="#fff"
                  strokeWidth={1}
                />
              </InfoToolTip>
              {hasOverlapActual && actualLineValue !== undefined && (
                <circle
                  cx={cx}
                  cy={props.yScale(actualLineValue)}
                  r={DOT_SIZE / 2}
                  fill={config.colors.line}
                  stroke="#fff"
                  strokeWidth={1}
                />
              )}
            </motion.g>
          );
        })}

        {/* Bar-top dots + dashed line, for charts with no lineValue series (e.g. MoM breakdown) */}
        {!hasLineValueSeries &&
          orderedBars.map((bar, index) => {
            const cx = bar.x + bar.width / 2;
            const cy = props.yScale(bar.data.data.bar1);
            const forecastMeta = forecastMetaByDate?.[bar.data.data.date];

            const nextBar = orderedBars[index + 1];
            const hasNextPoint = Boolean(nextBar);
            const nextCx = nextBar ? nextBar.x + nextBar.width / 2 : 0;
            const nextCy = nextBar ? props.yScale(nextBar.data.data.bar1) : 0;

            return (
              <motion.g key={`bar-top-dot-${bar.key}`}>
                {hasNextPoint && (
                  <motion.line
                    strokeDasharray="5 4"
                    x1={cx}
                    y1={cy}
                    x2={nextCx}
                    y2={nextCy}
                    stroke={config.colors.bar1}
                    strokeWidth={2}
                  />
                )}
                <InfoToolTip
                  data={bar.data.data}
                  config={config}
                  onAmountClick={onAmountClick}
                  forecastMeta={forecastMeta}
                >
                  <circle
                    cx={cx}
                    cy={cy}
                    r={DOT_SIZE / 2}
                    fill={config.colors.bar1}
                    stroke="#fff"
                    strokeWidth={1}
                  />
                </InfoToolTip>
              </motion.g>
            );
          })}
      </>
    );
  }
);

export const createBar = (
  config: BarChartConfig,
  onAmountClick?: AmountClickHandler,
  forecastMetaByDate?: ForecastMetaByDate
) => {
  return (props: BarCustomLayerProps<GenericBarChartData>) => (
    <BarLayer
      props={props}
      config={config}
      onAmountClick={onAmountClick}
      forecastMetaByDate={forecastMetaByDate}
    />
  );
};

export const Grid = () => {
  return (
    <g>
      <line
        strokeDasharray="4 4"
        opacity="1"
        x1="10"
        x2="511.5"
        y1="130"
        y2="130"
        stroke={colors.neutral[0]}
        strokeWidth="1"
      />
      <line
        strokeDasharray="4 4"
        opacity="1"
        x1="10"
        x2="511.5"
        y1="-10"
        y2="-10"
        stroke={colors.neutral[0]}
        strokeWidth="1"
      />
    </g>
  );
};

// Format large numbers with K/M suffix
const formatAxisValue = (value: number): string => {
  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  if (absValue >= 1_000_000) {
    return `${sign}$${(absValue / 1_000_000).toFixed(1)}M`;
  }
  if (absValue >= 1_000) {
    return `${sign}$${Math.round(absValue / 1_000)}K`;
  }
  return `${sign}$${absValue}`;
};

// Vertical month labels so they don't overlap on narrow screens.
const MOBILE_TICK_ROTATION = -90;

export const BarChart = ({
  data,
  config,
  showLeftAxis = false,
  showAllMonths = false,
  onAmountClick,
  forecastMetaByDate,
}: {
  data: GenericBarChartData[];
  config: BarChartConfig;
  showLeftAxis?: boolean;
  showAllMonths?: boolean;
  onAmountClick?: AmountClickHandler;
  forecastMetaByDate?: ForecastMetaByDate;
}) => {
  const { isMobile } = useWindowSize();
  const Bar = useMemo(
    () => createBar(config, onAmountClick, forecastMetaByDate),
    [config, onAmountClick, forecastMetaByDate]
  );

  // Calculate scale based on bar values only (not lineValue)
  const barValues = data.flatMap((d) => [d.bar1, d.bar2]);
  const barMin = Math.min(...barValues, 0);
  const barMax = Math.max(...barValues, 0);

  // Calculate nice round tick interval (100, 200, 500, 1000, 2000, 5000, etc.)
  const getNiceTickInterval = (value: number): number => {
    const absValue = Math.abs(value);
    if (absValue <= 0) return 100;
    const magnitude = Math.pow(10, Math.floor(Math.log10(absValue)));
    const normalized = absValue / magnitude;
    if (normalized <= 2) return magnitude;
    if (normalized <= 5) return magnitude * 2;
    return magnitude * 5;
  };

  // Calculate asymmetric scale bounds
  const maxTickInterval = getNiceTickInterval(
    Math.max(Math.abs(barMin), Math.abs(barMax))
  );
  const scaleMin =
    barMin < 0 ? Math.floor(barMin / maxTickInterval) * maxTickInterval : 0;
  const scaleMax =
    barMax > 0 ? Math.ceil(barMax / maxTickInterval) * maxTickInterval : 0;

  // Generate tick values from min to max
  const generateTickValues = (
    min: number,
    max: number,
    interval: number
  ): number[] => {
    const ticks: number[] = [];
    for (let i = min; i <= max; i += interval) {
      ticks.push(i);
    }
    if (!ticks.includes(0)) ticks.push(0);
    return ticks.sort((a, b) => a - b);
  };

  const tickValues = generateTickValues(scaleMin, scaleMax, maxTickInterval);

  return (
    <ResponsiveBar
      padding={0.2}
      borderRadius={BAR_RADIUS}
      data={data}
      keys={["bar1", "bar2"]}
      indexBy="date"
      margin={{ bottom: 40, top: 10, left: showLeftAxis ? 50 : 0, right: 0 }}
      colors={[config.colors.bar1, config.colors.bar2]}
      axisBottom={{
        tickSize: 0,
        tickPadding: 10,
        tickRotation: isMobile ? MOBILE_TICK_ROTATION : 0,
        format: (value) => dayjs(value).format("MMM"),
      }}
      axisLeft={
        showLeftAxis
          ? {
              tickSize: 0,
              tickPadding: 10,
              tickValues: tickValues,
              format: formatAxisValue,
            }
          : null
      }
      enableLabel={false}
      enableGridX={false}
      enableGridY={true}
      gridYValues={tickValues}
      layers={[
        "grid",
        "axes",
        Bar,
        "totals",
        "markers",
        "legends",
        "annotations",
      ]}
      valueScale={{
        type: "linear",
        min: scaleMin,
        max: scaleMax,
      }}
      theme={{
        grid: {
          line: {
            stroke: colors.neutral[0],
            strokeDasharray: "4 4",
          },
        },
        axis: {
          ticks: {
            text: {
              fill: colors.text[30],
              fontSize: 12,
            },
          },
        },
      }}
    />
  );
};

const MetricsItem = ({
  title,
  value,
  subTitle,
  forecastValue,
  forecastLabel,
  forecastDirection = "up",
  forecastInvertColors = false,
}: {
  title: ReactNode;
  value: ReactNode;
  subTitle?: ReactNode;
  forecastValue?: ReactNode;
  forecastLabel?: string;
  forecastDirection?: "up" | "down";
  forecastInvertColors?: boolean;
}) => {
  return (
    <div className="t-flex t-flex-col t-gap-2 t-w-full sm:t-min-w-0 sm:t-gap-1">
      <div className="t-text-body-sm t-text-text-30">{title}</div>
      <div className="t-text-subtext sm:t-text-subtext-sm t-text-text-60">
        {value}
      </div>
      {forecastValue ? (
        <ForecastSubtitle
          value={forecastValue}
          label={forecastLabel}
          direction={forecastDirection}
          invertColors={forecastInvertColors}
        />
      ) : (
        subTitle
      )}
    </div>
  );
};

const Divider = () => {
  return (
    <div className="t-h-6 t-border-l t-border-r-0 t-border-solid t-border-neutral-10" />
  );
};

export type MetricsForecastTotals = {
  revenue: number;
  expense: number;
  profit: number;
};

export const MetricsReport = ({
  data,
  periodText,
  forecastTotals,
}: {
  data: Metrics;
  periodText?: string;
  forecastTotals?: MetricsForecastTotals;
}) => {
  const profitLoss = Number(data.total_revenue) - Number(data.gross_burn);
  const isProfit = profitLoss >= 0;
  const isForecastProfit = (forecastTotals?.profit ?? 0) >= 0;

  const revenuePercentage = data.total_revenue_change_percentage;
  const expensePercentage = data.gross_burn_change_percentage;
  const profitLossPercentage = data.profit_loss_change_percentage;

  return (
    <div className="t-flex t-gap-6 t-justify-between t-items-center sm:t-gap-2">
      <div className="t-flex t-gap-6 t-justify-between t-items-center t-w-full sm:t-gap-2">
        <MetricsItem
          title="Revenue"
          value={
            <HideableAmount>
              {formatAmount(Number(data.total_revenue))}
            </HideableAmount>
          }
          forecastValue={
            forecastTotals && (
              <HideableAmount>
                {formatAmount(forecastTotals.revenue)}
              </HideableAmount>
            )
          }
          forecastDirection="up"
          subTitle={
            periodText && (
              <span className="t-text-body-sm sm:t-text-caption">
                <TrendSubtitle
                  percentage={
                    revenuePercentage != null
                      ? Math.abs(revenuePercentage)
                      : null
                  }
                  period={periodText}
                  direction={(revenuePercentage ?? 0) >= 0 ? "up" : "down"}
                />
              </span>
            )
          }
        />
        <Divider />
      </div>
      <div className="t-flex t-gap-6 t-justify-between t-items-center t-w-full sm:t-gap-2">
        <MetricsItem
          title="Expense"
          value={
            <HideableAmount>
              {formatAmount(Number(data.gross_burn))}
            </HideableAmount>
          }
          forecastValue={
            forecastTotals && (
              <HideableAmount>
                {formatAmount(Math.abs(forecastTotals.expense))}
              </HideableAmount>
            )
          }
          forecastDirection="up"
          forecastInvertColors
          subTitle={
            periodText && (
              <span className="t-text-body-sm sm:t-text-caption">
                <TrendSubtitle
                  percentage={
                    expensePercentage != null
                      ? Math.abs(expensePercentage)
                      : null
                  }
                  period={periodText}
                  direction={(expensePercentage ?? 0) >= 0 ? "up" : "down"}
                  invertColors
                />
              </span>
            )
          }
        />
        <Divider />
      </div>
      <MetricsItem
        title={isProfit ? "Profit" : "Loss"}
        value={
          <HideableAmount>{formatAmount(Number(profitLoss))}</HideableAmount>
        }
        forecastValue={
          forecastTotals && (
            <HideableAmount>
              {formatAmount(forecastTotals.profit)}
            </HideableAmount>
          )
        }
        forecastLabel={
          isForecastProfit ? "forecasted profit" : "forecasted loss"
        }
        forecastDirection={isForecastProfit ? "up" : "down"}
        subTitle={
          periodText && (
            <span className="t-text-body-sm sm:t-text-caption">
              <TrendSubtitle
                percentage={
                  profitLossPercentage != null
                    ? Math.abs(profitLossPercentage)
                    : null
                }
                period={periodText}
                direction={(profitLossPercentage ?? 0) >= 0 ? "up" : "down"}
                invertColors={!isProfit}
              />
            </span>
          )
        }
      />
    </div>
  );
};

// Transform RevenueMetrics to GenericBarChartData
const transformRevenueMetrics = (
  data: RevenueMetrics[]
): GenericBarChartData[] =>
  data.map((item) => ({
    date: item.period,
    bar1: item.revenue,
    bar2: item.expense,
    lineValue: item.profit,
  }));

const getRevenueMetricsForecastFields = (
  item: RevenueMetrics
): BarForecastMeta => ({
  isForecast: item.is_forecast,
  hasOverlapActual: item.has_overlap_actual,
  actualBar1: item.actual_revenue ?? undefined,
  actualBar2: item.actual_expense ?? undefined,
  actualLineValue: item.actual_profit ?? undefined,
});

export const CashFlowChart = ({
  data,
  onAmountClick,
}: {
  data: RevenueMetrics[];
  onAmountClick?: AmountClickHandler;
}) => {
  const chartData = transformRevenueMetrics(data);
  const { forecastMetaByDate, hasForecastData } = buildForecastMetaByDate(
    data,
    (item) => item.period,
    getRevenueMetricsForecastFields
  );

  return (
    <>
      <div className="t-w-full t-border-t-[1px] t-border-b-0 t-border-solid t-border-neutral-10" />
      <div className="t-flex t-gap-4 t-items-center t-mb-4">
        <Legend dot={<LegendDot color={REVENUE_BAR_COLOR} />} lable="Revenue" />
        <Legend dot={<LegendDot color={EXPENSE_BAR_COLOR} />} lable="Expense" />
        <Legend
          dot={<LegendDot color={PROFIT_LOSS_LINE_COLOR} />}
          lable="Profit/Loss"
        />
        <ForecastLegend show={hasForecastData} />
      </div>
      <div className="t-h-44 t-w-full">
        <BarChart
          data={chartData}
          config={CASH_FLOW_CONFIG}
          showLeftAxis
          onAmountClick={onAmountClick}
          forecastMetaByDate={forecastMetaByDate}
        />
      </div>
    </>
  );
};
